import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Modal from '../components/Modal';
import TenantForm from '../components/TenantForm';
import PhoneCallForm from '../components/PhoneCallForm';
import DocumentList from '../components/DocumentList';
import { formatDateDMY } from '../components/DateInput';
import api from '../api';
import { tenantCreateErrorMessage, tenantLinkErrorMessage } from '../utils/tenantErrors';

const STATUS_MAP = {
  2:  { label: 'יש להשלים חתימה של הסכם ראשוני',              bg: '#fef9c3', color: '#854d0e' },
  3:  { label: 'דיור ציבורי',                                  bg: '#e0e7ff', color: '#3730a3' },
  4:  { label: 'מסרב לשתף פעולה',                             bg: '#fee2e2', color: '#991b1b' },
  5:  { label: 'בטיפול',                                       bg: '#fef9c3', color: '#854d0e' },
  6:  { label: 'לא נוצר קשר',                                  bg: '#f3f4f6', color: '#374151' },
  7:  { label: 'חתום על הסכם סופי',                            bg: '#dcfce7', color: '#166534' },
  8:  { label: 'חתום עם בעיית רישום',                          bg: '#ffedd5', color: '#9a3412' },
  9:  { label: 'יש להשלים חתימה',                              bg: '#fef9c3', color: '#854d0e' },
  10: { label: 'קיבל הסכם',                                    bg: '#dbeafe', color: '#1e40af' },
  12: { label: 'יש לתאם חתימה',                                bg: '#fef9c3', color: '#854d0e' },
  13: { label: 'תואמה חתימה',                                  bg: '#dcfce7', color: '#166534' },
  14: { label: 'רוצים לחתום אך יש בעיה משפטית',               bg: '#fee2e2', color: '#991b1b' },
  15: { label: 'חתום על נון שופ - יהודה לוי',                 bg: '#dcfce7', color: '#166534' },
  16: { label: 'חתום על הסכם סופי - יהודה לוי',               bg: '#dcfce7', color: '#166534' },
  17: { label: 'חתום על הסכם סופי עם הערת אזהרה - יהודה לוי', bg: '#dcfce7', color: '#166534' },
  18: { label: 'לא יודע על מה חתום - יהודה לוי',              bg: '#f3f4f6', color: '#374151' },
  19: { label: 'מגיע לכנס הצגת הסכם סקייליין',                bg: '#dbeafe', color: '#1e40af' },
  20: { label: 'גר בחוץ לארץ',                                 bg: '#e0e7ff', color: '#3730a3' },
};

function StatusBadge({ value }) {
  const s = STATUS_MAP[value];
  if (!s) return <span>{value ?? '—'}</span>;
  return (
    <span style={{
      padding: '2px 8px',
      borderRadius: 10,
      fontSize: '0.8rem',
      fontWeight: 'bold',
      background: s.bg,
      color: s.color,
      whiteSpace: 'nowrap'
    }}>
      {s.label}
    </span>
  );
}

export default function ApartmentDetail() {
  const { addr, num, aptNum } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('tenants');
  const [tenants, setTenants] = useState([]);
  const [calls, setCalls] = useState([]);
  const [docs, setDocs] = useState([]);
  const [modal, setModal] = useState(null);
  const googleDriveFolderId = sessionStorage.getItem('googleDriveFolderId');

  useEffect(() => {
    fetchTenants();
    fetchCalls();
    fetchDocs();
  }, [addr, num, aptNum]);

  async function fetchTenants() {
    const res = await api.get(`/tenants/apartment/${addr}/${num}/${aptNum}`);
    setTenants(res.data);
  }

  async function fetchCalls() {
    const res = await api.get(`/phonecalls/${addr}/${num}/${aptNum}`);
    setCalls(res.data);
  }

  async function fetchDocs() {
    const res = await api.get(`/documents/${addr}/${num}`);
    setDocs(res.data);
  }

  async function handleCreateTenant(form) {
    try {
      await api.post('/tenants', form);
    } catch (err) {
      alert(tenantCreateErrorMessage(err, form.id));
      return;
    }
    try {
      await api.post('/tenants/apartment', {
        id: form.id,
        buildingAddress: decodeURIComponent(addr),
        buildingNumber: num,
        apartmentNumber: aptNum
      });
    } catch (err) {
      alert(tenantLinkErrorMessage(err));
      return;
    }
    setModal(null);
    fetchTenants();
  }

  async function handleLinkTenant(e) {
    e.preventDefault();
    const id = e.target.tenantId.value;
    await api.post('/tenants/apartment', {
      id,
      buildingAddress: decodeURIComponent(addr),
      buildingNumber: num,
      apartmentNumber: aptNum
    });
    setModal(null);
    fetchTenants();
  }

  async function handleEditTenant(form) {
    try {
      await api.put(`/tenants/${modal.data.id}`, form);
      setModal(null);
      fetchTenants();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בעדכון דייר');
    }
  }

  async function handleUnlinkTenant(id) {
    if (!window.confirm('להסיר דייר מהדירה?')) return;
    await api.delete(`/tenants/apartment/${addr}/${num}/${aptNum}/${id}`);
    fetchTenants();
  }

  async function handleAddCall(form) {
    try {
      await api.post('/phonecalls', {
        ...form,
        buildingAddress: decodeURIComponent(addr),
        buildingNumber: num,
        apartmentNumber: aptNum,
        workerFirstName: localStorage.getItem('workerFirstName'),
        workerLastName: localStorage.getItem('workerLastName')
      });
      setModal(null);
      fetchCalls();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בשמירת השיחה');
    }
  }

  async function handleEditCall(form) {
    try {
      await api.put(`/phonecalls/${modal.data.id}`, form);
      setModal(null);
      fetchCalls();
    } catch (err) {
      alert(err.response?.data?.error || 'שגיאה בעדכון השיחה');
    }
  }

  const tabs = [
    { id: 'tenants', label: 'דיירים' },
    { id: 'calls', label: 'שיחות טלפון' },
    { id: 'docs', label: 'מסמכים' }
  ];

  return (
    <div>
      <Navbar
        title={`דירה ${aptNum} - ${decodeURIComponent(addr)} ${num}`}
        breadcrumb="פרויקטים > בניינים > דירות > פרטי דירה"
      />
      <div className="container">
        <div style={{ marginBottom: 16 }}>
          <button className="btn btn-secondary" onClick={() => navigate(-1)}>← חזור</button>
        </div>
        <div className="tabs">
          {tabs.map(t => (
            <div key={t.id} className={`tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
              {t.label}
            </div>
          ))}
        </div>
        <div className="tab-content">
          {activeTab === 'tenants' && (
            <div>
              <div className="actions-row">
                <button className="btn btn-primary" onClick={() => setModal({ type: 'newTenant' })}>+ דייר חדש</button>
                <button className="btn btn-secondary" onClick={() => setModal({ type: 'linkTenant' })}>קשר דייר קיים</button>
              </div>
              {tenants.length === 0 ? <p>אין דיירים</p> : (
                <table>
                  <thead>
                    <tr><th>ת.ז.</th><th>שם פרטי</th><th>שם משפחה</th><th>נייד</th><th>טלפון בית</th><th>פקס</th><th>אימייל</th><th>סוג דייר</th><th>סטטוס</th><th>% החזקה</th><th>איש קשר</th><th>הערות</th><th>פעולות</th></tr>
                  </thead>
                  <tbody>
                    {tenants.map(t => (
                      <tr key={t.id}>
                        <td>{t.id}</td>
                        <td>{t.firstName}</td>
                        <td>{t.lastName}</td>
                        <td>
                          {t.cellularPhone}
                          {t.cellularPhone && (
                            <a
                              href={`https://wa.me/972${t.cellularPhone.replace(/^0/, '').replace(/\D/g, '')}`}
                              target="_blank"
                              rel="noreferrer"
                              style={{ marginRight: 8, fontSize: '0.85rem', color: '#16a34a' }}
                            >
                              💬 WhatsApp
                            </a>
                          )}
                        </td>
                        <td>{t.homePhone}</td>
                        <td>{t.fax}</td>
                        <td>{t.email}</td>
                        <td>{t.tenantType}</td>
                        <td><StatusBadge value={t.tenantStatus} /></td>
                        <td>{t.percentageOfHOldingInApartment}</td>
                        <td>{t.isContactPerson ? 'כן' : ''}</td>
                        <td>{t.tenantNotes}</td>
                        <td>
                          <button className="btn btn-secondary" style={{ marginLeft: 4 }} onClick={() => setModal({ type: 'editTenant', data: t })}>ערוך</button>
                          <button className="btn btn-danger" onClick={() => handleUnlinkTenant(t.id)}>הסר</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'calls' && (
            <div>
              <div className="actions-row">
                <button className="btn btn-primary" onClick={() => setModal({ type: 'newCall' })}>+ שיחה חדשה</button>
              </div>
              {calls.length === 0 ? <p>אין שיחות</p> : (
                <table>
                  <thead><tr><th>תאריך שיחה</th><th>תיאור</th><th>תאריך מעקב</th><th>פעולות</th></tr></thead>
                  <tbody>
                    {calls.map((c, i) => (
                      <tr key={i}>
                        <td>{c.phoneCallDate ? formatDateDMY(c.phoneCallDate) : ''}</td>
                        <td>{c.phoneCallNote}</td>
                        <td>{c.followUpDate ? formatDateDMY(c.followUpDate) : ''}</td>
                        <td><button className="btn btn-secondary" onClick={() => setModal({ type: 'editCall', data: c })}>ערוך</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'docs' && (
            <DocumentList
              docs={docs}
              buildingAddress={decodeURIComponent(addr)}
              buildingNumber={num}
              apartmentNumber={aptNum}
              onRefresh={fetchDocs}
            />
          )}
        </div>
      </div>

      {modal?.type === 'newTenant' && (
        <Modal title="דייר חדש" onClose={() => setModal(null)}>
          <TenantForm onSubmit={handleCreateTenant} onCancel={() => setModal(null)} />
        </Modal>
      )}
      {modal?.type === 'linkTenant' && (
        <Modal title="קשר דייר קיים" onClose={() => setModal(null)}>
          <form onSubmit={handleLinkTenant}>
            <div className="form-group">
              <label>תעודת זהות דייר</label>
              <input name="tenantId" required />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button type="submit" className="btn btn-primary">קשר</button>
              <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>ביטול</button>
            </div>
          </form>
        </Modal>
      )}
      {modal?.type === 'editTenant' && (
        <Modal title="ערוך דייר" onClose={() => setModal(null)}>
          {googleDriveFolderId && (
            <a
              href={`https://drive.google.com/drive/folders/${googleDriveFolderId}?q=${modal.data.id}`}
              target="_blank"
              rel="noreferrer"
              style={{
                display: 'inline-block',
                marginBottom: 16,
                padding: '6px 14px',
                background: '#1a56db',
                color: '#fff',
                borderRadius: 6,
                textDecoration: 'none',
                fontSize: '0.85rem',
                fontWeight: 600
              }}
            >
              תמונת תעודת זהות
            </a>
          )}
          <TenantForm initial={modal.data} isEdit onSubmit={handleEditTenant} onCancel={() => setModal(null)} />
        </Modal>
      )}
      {modal?.type === 'newCall' && (
        <Modal title="שיחת טלפון חדשה" onClose={() => setModal(null)}>
          <PhoneCallForm onSubmit={handleAddCall} onCancel={() => setModal(null)} />
        </Modal>
      )}
      {modal?.type === 'editCall' && (
        <Modal title="ערוך שיחת טלפון" onClose={() => setModal(null)}>
          <PhoneCallForm initial={modal.data} onSubmit={handleEditCall} onCancel={() => setModal(null)} />
        </Modal>
      )}
    </div>
  );
}
